import { queryKeys } from '../keys'
import { DEFAULT_RETRY_MUTATION } from '../config'
import type { QueryClient } from '@tanstack/react-query'
import type {
  GetBookingDetailResponse,
  GetBookingTransportationResponse,
} from '@/types/booking'
import { stepService } from '@/services/step'

function updateStep(queryClient: QueryClient) {
  return queryClient.setMutationDefaults(queryKeys.step.update(), {
    retry: DEFAULT_RETRY_MUTATION,
    mutationFn: (payload: Parameters<typeof stepService.update>[0]) =>
      stepService.update(payload),
    onMutate: (payload) => {
      const existingBooking =
        queryClient.getQueryData<GetBookingDetailResponse>(
          queryKeys.booking.detail(payload.id),
        )
      const existingTransportation =
        queryClient.getQueryData<GetBookingTransportationResponse>(
          queryKeys.booking.transportation(payload.id),
        )

      const optimistic = {
        ...(payload.type === 'booked_transportation' && existingTransportation
          ? {
              ...existingTransportation,
              data: {
                ...existingTransportation.data,
                booked_transportation: {
                  ...existingTransportation.data.booked_transportation,
                  step: payload.step as any,
                },
              },
            }
          : {}),
        ...(payload.type === 'booking' && existingBooking
          ? {
              ...existingBooking,
              data: {
                ...existingBooking.data,
                booking: {
                  ...existingBooking.data.booking,
                  step: payload.step as any,
                },
              },
            }
          : {}),
      } as GetBookingDetailResponse | GetBookingTransportationResponse

      if (payload.type === 'booked_transportation' && existingTransportation) {
        queryClient.setQueryData<GetBookingTransportationResponse>(
          queryKeys.booking.transportation(payload.id),
          optimistic as GetBookingTransportationResponse,
        )
      }

      if (payload.type === 'booking' && existingBooking) {
        queryClient.setQueryData<GetBookingDetailResponse>(
          queryKeys.booking.detail(payload.id),
          optimistic as GetBookingDetailResponse,
        )
      }

      return { existingBooking, existingTransportation }
    },
    onError: (_, payload, context) => {
      if (context?.existingBooking) {
        queryClient.setQueryData<GetBookingDetailResponse>(
          queryKeys.booking.detail(payload.id),
          context.existingBooking,
        )
      }
      if (context?.existingTransportation) {
        queryClient.setQueryData<GetBookingTransportationResponse>(
          queryKeys.booking.transportation(payload.id),
          context.existingTransportation,
        )
      }
    },
  })
}

export function registerStepMutations(queryClient: QueryClient) {
  updateStep(queryClient)
}
